/* #region header */
/**************************************************************************************************
//
//  Description: user Context
//
//  Copyright:    © 2022 Aligned Assets Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier            Issue# Description
//#region Version 1.0.0.0 changes
//    001   28.01.22 Sean Flook         WI39??? Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
/* #endregion header */

/* #region imports */

import React, { useEffect, useState } from "react";
import { WhoAmI } from "./../configuration/AAConfig_Security";

/* #endregion imports */

const UserContext = React.createContext();
UserContext.displayName = "userContext";

const UserContextProvider = ({ children }) => {
  const tokenStorageName = "curToken";

  const GetLocalStorage = (key) => {
    const val = localStorage.getItem(tokenStorageName);
    return !val || val === "null" ? null : val;
  };

  const [loginDetails, setLoginDetails] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userError, setUserError] = useState(null);
  const [useAuthenticate, setUseAuthenticate] = useState(null);
  const [currentToken, setCurrentToken] = useState(GetLocalStorage(tokenStorageName));
  const [authenticateId, setAuthenticateId] = useState(null);
  const [resetCode, setResetCode] = useState(null);
  const [showAuthenticate, setShowAuthenticate] = useState(
    authenticateId !== null && !currentToken
  );
  const [showLogin, setShowLogin] = useState(!currentToken);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);

  useEffect(() => {
    const storedtokenData = GetLocalStorage(tokenStorageName);

    if (currentToken && storedtokenData !== currentToken) {
      localStorage.setItem(tokenStorageName, currentToken);
    }

    if (!currentToken && storedtokenData) {
      setShowLogin(false);
      setCurrentToken(storedtokenData);
    }

    async function GetUser() {
      const userInfo = await WhoAmI(currentToken, setUserError);

      if (userInfo && userInfo.rights) {
        if (
          userInfo.rights.findIndex(
            (x) => x === "SuperAdministrator" || x === "OrganisationAdministrator"
          ) < 0
        ) {
          setUserError("You dont have sufficient rights to edit other users");
        } else {
          if (userInfo.active && !userInfo.isDeleted) {
            setUserError(null);
            setCurrentUser(userInfo);
          } else {
            updateCurrentUser(null);
            setUserError(
              !userInfo.active
                ? "You are not an active user on this system."
                : "This user has been deleted."
            );
          }
        }
      } else {
        setUserError("Unable to get user information.");
      }
    }

    if (currentToken && !currentUser) {
      GetUser();
    }
  }, [currentToken, currentUser]);

  useEffect(() => {
    if (loginDetails && loginDetails.token) {
      setCurrentToken(loginDetails.token);
    } else {
      setCurrentUser(null);
      setCurrentToken(null);
    }
  }, [loginDetails]);

  useEffect(() => {
    setShowLogin(!currentUser);
    if (useAuthenticate)
      setShowAuthenticate(authenticateId !== null && !currentUser);
  }, [authenticateId, currentUser, useAuthenticate]);

  useEffect(() => {
    if (useAuthenticate) {
      setShowLogin(!(authenticateId !== null));
      setShowAuthenticate(!!authenticateId);
    }
  }, [authenticateId, useAuthenticate]);

  useEffect(() => {
    if (userError && userError.length > 0) {
      setLoginDetails(null);
      localStorage.setItem(tokenStorageName, null);
      setCurrentUser(null);
      if (useAuthenticate) {
        setAuthenticateId(null);
        setShowAuthenticate(false);
      }
      setShowLogin(true);
    }
  }, [useAuthenticate, userError]);

  function logoff(reload) {
    console.log(`loggoff called`);
    setLoginDetails(null);
    localStorage.setItem(tokenStorageName, null);

    if (reload) window.location.reload();
  }

  function updateLoginDetails(newLogin) {
    setLoginDetails(newLogin);
  }

  function updateCurrentUser(newUser) {
    setCurrentUser(newUser);
  }

  function updateUserError(error) {
    setUserError(error);
  }

  function updateShowLogin(newshowLogin) {
    setShowLogin(newshowLogin);
  }

  function updateAuthenticateId(newAuthenticateId) {
    setAuthenticateId(newAuthenticateId);
  }

  function updateResetCode(code) {
    setResetCode(code);
  }

  function updateShowAuthenticate(newshowAuthenticate) {
    setShowAuthenticate(newshowAuthenticate);
  }

  function updateUseAuthenticate(newUseAuthenticate) {
    setUseAuthenticate(newUseAuthenticate);
  }

  function updateShowResetPasswordDialog(val) {
    setShowResetPasswordDialog(val);
  }

  return (
    <UserContext.Provider
      value={{
        currentToken,
        currentUser,
        useAuthenticate,
        authenticateId,
        resetCode,
        showLogin,
        showAuthenticate,
        userError,
        logoff,
        setShowAuthenticate,
        updateUserError,
        updateLoginDetails,
        updateCurrentUser,
        updateShowLogin,
        updateAuthenticateId,
        updateResetCode,
        updateShowAuthenticate,
        updateUseAuthenticate,
        showResetPasswordDialog,
        updateShowResetPasswordDialog,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserContextProvider, UserContext };
